import type {
  AmmTelemetryView,
  AmmVenueState,
  Candle,
  CandleInterval,
  CandleSeries,
  Instrument,
  InstrumentProfile,
  InstrumentTelemetryView,
  PairListing,
  Percent,
  Rate,
  TradeView,
} from '@nutrimatic/sdk'

import type {
  MarketCandle,
  MarketTokenProfile,
  OhlcvInterval,
  PairDetail,
  PairSummary,
  RecentTrade,
  TokenSocialKind,
  TokenSocialLink,
} from '@/data/marketApi/types'
import type { PoolIdentity, PoolMetrics, PoolSummary } from '@/data/marketApi/poolsTypes'
import { EMPTY_POOL_METRICS } from '@/data/marketApi/poolsTypes'
import { formatPairLabel, toDisplaySymbol } from '@/data/marketApi/normalizeMarketToken'
import type { ChainId } from '@/sdk'

import { poolAddressFromVenueId, type PairBridgeEntry } from './ids'

const V2_FEE = '0.30%'

function formatUsd(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`
  return `$${n.toFixed(2)}`
}

function formatPrice(rate: Rate | undefined | null): string {
  if (!rate) return '—'
  const v = typeof rate.value === 'number' ? rate.value : Number(rate.value)
  if (!Number.isFinite(v)) return String(rate.value)
  if (v >= 1000) return v.toFixed(2)
  if (v >= 1) return v.toFixed(4)
  return v.toPrecision(4)
}

function formatChange(change: Percent | undefined | null): string {
  if (change == null || !Number.isFinite(change.value)) return '—'
  const pct = change.value * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

function quantityDisplay(raw: { raw: bigint | string; decimals: number } | undefined): string {
  if (!raw) return '—'
  try {
    const value = typeof raw.raw === 'bigint' ? raw.raw : BigInt(raw.raw)
    const denom = 10n ** BigInt(raw.decimals)
    const whole = value / denom
    const frac = value % denom
    if (frac === 0n) return whole.toString()
    const fracStr = frac.toString().padStart(raw.decimals, '0').replace(/0+$/, '')
    return fracStr ? `${whole}.${fracStr}` : whole.toString()
  } catch {
    return '—'
  }
}

export function mapCandleInterval(interval: OhlcvInterval | string): CandleInterval {
  if (interval === '5m') return '15m'
  if (interval === '15m' || interval === '1h' || interval === '4h' || interval === '1d' || interval === '1w') {
    return interval
  }
  return '1h'
}

export function mapCandlesToMarket(series: CandleSeries | null | undefined): MarketCandle[] {
  if (!series) return []
  const closed = series.candles.map(candleToMarket)
  if (series.partialCandle) {
    return [...closed, candleToMarket(series.partialCandle)]
  }
  return closed
}

function candleToMarket(c: Candle): MarketCandle {
  return {
    time: c.time,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
  }
}

export function mapTradesToRecent(events: readonly TradeView[] | undefined): RecentTrade[] {
  if (!events?.length) return []
  return [...events]
    .slice()
    .reverse()
    .map((t) => ({
      id: t.eventId,
      price: String(t.price),
      amount: String(t.baseAmount),
      side: t.side,
      time: new Date(t.time * 1000).toISOString(),
      txHash: t.txHash,
    }))
}

function linkKind(label: string): TokenSocialKind | 'website' | 'whitepaper' | null {
  const l = label.toLowerCase()
  if (l === 'website' || l === 'whitepaper') return l
  if (l === 'x' || l === 'twitter') return 'twitter'
  if (l === 'telegram' || l === 'discord' || l === 'github') return l
  return null
}

export function mapInstrumentToTokenProfile(
  instrument: Instrument,
  profile: InstrumentProfile | null | undefined,
  telemetry: InstrumentTelemetryView | null | undefined,
  address: string,
): MarketTokenProfile {
  const socials: TokenSocialLink[] = []
  let website: string | undefined
  let whitepaper: string | undefined
  for (const link of profile?.links ?? []) {
    const kind = linkKind(link.label)
    if (kind === 'website') {
      website = link.url
      continue
    }
    if (kind === 'whitepaper') {
      whitepaper = link.url
      continue
    }
    if (kind) {
      socials.push({ kind, label: link.label, href: link.url })
    }
  }

  const supply = telemetry?.supply
  const circulating = supply?.derived?.circulating
  const total = supply?.total

  return {
    address,
    symbol: instrument.symbol,
    name: instrument.displayName ?? instrument.symbol,
    decimals: instrument.decimals ?? 18,
    description: profile?.description ?? profile?.tagline ?? '',
    iconUrl: profile?.logoUrl ?? '',
    bannerUrl: profile?.headerUrl ?? '',
    tags: profile?.tags ? [...profile.tags] : [],
    website,
    whitepaper,
    socials,
    priceUsd: telemetry?.priceUsd != null ? String(telemetry.priceUsd) : undefined,
    change24h: formatChange(telemetry?.change24h),
    marketCap:
      supply?.derived?.mcapCirculating != null ? String(supply.derived.mcapCirculating) : undefined,
    volume24h:
      telemetry?.market?.volume24hUsd != null ? String(telemetry.market.volume24hUsd) : undefined,
    totalSupply: total ? quantityDisplay(total) : undefined,
    circulatingSupply: circulating ? quantityDisplay(circulating) : undefined,
    holders: supply?.holders?.count != null ? String(supply.holders.count) : undefined,
    launchedAt: profile?.listedAt,
  }
}

export function mapListingToPairSummary(
  listing: PairListing,
  view: AmmTelemetryView | null | undefined,
  base: Instrument | undefined,
  quote: Instrument | undefined,
  pairAddress: string,
  chainId: ChainId,
): PairSummary {
  const token0Symbol = toDisplaySymbol(
    base?.onChainAddress ?? '',
    base?.symbol ?? listing.baseInstrumentId.split(':').pop() ?? '?',
    chainId,
  )
  const token1Symbol = toDisplaySymbol(
    quote?.onChainAddress ?? '',
    quote?.symbol ?? listing.quoteInstrumentId.split(':').pop() ?? '?',
    chainId,
  )
  const token0Address = base?.onChainAddress ?? ''
  const token1Address = quote?.onChainAddress ?? ''

  return {
    pairAddress,
    pair: formatPairLabel(token0Address, token0Symbol, token1Address, token1Symbol, chainId),
    token0Address,
    token1Address,
    token0Symbol,
    token1Symbol,
    price: formatPrice(view?.spot),
    change24h: formatChange(view?.change24h),
    volume24h: formatUsd(view?.volume24hUsd),
    tvlUsd: view?.tvlUsd,
  }
}

export function mapPoolViewToMetrics(
  view: AmmTelemetryView | null | undefined,
  state: AmmVenueState | null | undefined,
  baseSymbol: string,
  quoteSymbol: string,
): PoolMetrics {
  const r0 = state?.reserves?.[0]
  const r1 = state?.reserves?.[1]
  return {
    ...EMPTY_POOL_METRICS,
    price: formatPrice(view?.spot ?? state?.spot),
    change24h: formatChange(view?.change24h),
    tvl: formatUsd(view?.tvlUsd),
    tvlUsd: view?.tvlUsd ?? 0,
    volume24h: formatUsd(view?.volume24hUsd),
    fee: V2_FEE,
    reserves0: r0 ? `${quantityDisplay(r0)} ${baseSymbol}` : '',
    reserves1: r1 ? `${quantityDisplay(r1)} ${quoteSymbol}` : '',
    apy: '—',
  }
}

export function composePoolSummaryFromNutrimatic(
  identity: PoolIdentity,
  view: AmmTelemetryView | null | undefined,
  state: AmmVenueState | null | undefined,
): PoolSummary {
  const metrics = mapPoolViewToMetrics(
    view,
    state,
    identity.token0.symbol,
    identity.token1.symbol,
  )
  return { ...identity, ...metrics }
}

export function mapPairDetailFromNutrimatic(
  bridge: PairBridgeEntry,
  identity: PoolIdentity | undefined,
  view: AmmTelemetryView | null | undefined,
  state: AmmVenueState | null | undefined,
  chainId: ChainId,
): PairDetail {
  const token0Addr = identity?.token0.address ?? ''
  const token1Addr = identity?.token1.address ?? ''
  const token0Sym = identity?.token0.symbol ?? 'TOKEN0'
  const token1Sym = identity?.token1.symbol ?? 'TOKEN1'
  const r0 = state?.reserves?.[0]
  const r1 = state?.reserves?.[1]

  return {
    pairAddress: bridge.pairAddress || poolAddressFromVenueId(bridge.venueId) || '',
    pair:
      identity?.pair ??
      formatPairLabel(token0Addr, token0Sym, token1Addr, token1Sym, chainId),
    token0: {
      address: token0Addr,
      symbol: token0Sym,
      decimals: 18,
    },
    token1: {
      address: token1Addr,
      symbol: token1Sym,
      decimals: 18,
    },
    reserves:
      r0 && r1
        ? {
            reserve0: quantityDisplay(r0),
            reserve1: quantityDisplay(r1),
            updatedAt: new Date(),
          }
        : null,
    price: formatPrice(view?.spot ?? state?.spot),
    change24h: formatChange(view?.change24h),
    volume24h: formatUsd(view?.volume24hUsd),
    tvlUsd: view?.tvlUsd ?? 0,
    trades24h: 0,
    apy: '—',
  }
}
